import { Chat } from "@app/shared/components/ai/Chat";
import { type MessageType } from "@app/utils/ai/chat";
import { type ScoreValue } from "@generated/client/graphql";
import { Fragment, useMemo, type FC } from "react";
import { useQuickAnswerChatTranslations } from "./i18n";
import { ReferenceBlock } from "./ReferenceBlock";

export const AiMessageBlock: FC<{
    parts: NonNullable<MessageType["parts"]>;
    status: "ready" | "streaming" | "submitted" | "error";
    onRegenerate?: () => void;
    onFeedback?: (scoreValue: ScoreValue) => void;
}> = ({ parts, onRegenerate, onFeedback, status }) => {
    const { t, keys } = useQuickAnswerChatTranslations();
    /**
     * During the tool calling, we need to show a loading message
     * while the tool is calling the API
     */
    const isCallingTool = useMemo(() => {
        if (status !== "streaming") {
            return false;
        }
        return parts.some(
            (part) =>
                part.type === "tool-invocation" &&
                part.toolInvocation.state !== "result",
        );
    }, [parts, status]);
    /**
     * After the tool calling, we need to show a loading message
     * while the tool is analysing the data
     */
    const lastPart = parts[parts.length - 1];
    const isAnalysing =
        lastPart.type === "tool-invocation" &&
        lastPart.toolInvocation.state === "result" &&
        status === "streaming";

    return (
        <>
            {parts.map((part, idx) =>
                part.type === "text" ? (
                    <Chat.AiMessage
                        key={"text-block" + part.text + idx}
                        withCopy
                        onRegenerate={onRegenerate}
                        onFeedback={onFeedback}
                    >
                        {part.text}
                    </Chat.AiMessage>
                ) : part.type === "tool-invocation" &&
                  part.toolInvocation.state === "result" ? (
                    <Fragment key={"reference-block" + idx}>
                        <ReferenceBlock
                            indicatorCitations={
                                part.toolInvocation.result.indicatorCitations
                            }
                            documentCitations={
                                part.toolInvocation.result.documentCitations
                            }
                        />
                    </Fragment>
                ) : null,
            )}
            {isCallingTool && (
                <Chat.LoadingMessage>
                    {t(keys.loading_tool)}
                </Chat.LoadingMessage>
            )}
            {isAnalysing && (
                <Chat.LoadingMessage>
                    {t(keys.loading_elaborating)}
                </Chat.LoadingMessage>
            )}
        </>
    );
};
